import { GenericTable } from "@shared/components/GenericTable";
import { TableAction } from "@shared/types/table";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useBillCycleMeterReadingMissing, useUpdateConsumerSelection } from "../../hooks";
import { Button } from "@shared/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { AlertTriangle } from "lucide-react";

interface MissingReadingsSubTabProps {
  onSelectionChange?: (selectedRows: any[]) => void;
  meterReadingMissingList?: any;
  billDraftId?: string;
}

export const MissingReadingsSubTab: React.FC<MissingReadingsSubTabProps> = ({
  onSelectionChange,
  meterReadingMissingList,
  billDraftId
}) => {
  const params = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMeters, setSelectedMeters] = useState<string[]>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ type: 'single' | 'all', meterNumber?: string } | null>(null);
  const [isExcluding, setIsExcluding] = useState(false);
  const { id } = useParams();

  const { mutateAsync: updateConsumer, isPending: isUpdating } =
    useUpdateConsumerSelection(billDraftId || id);

  const itemsPerPage = 50;

  const handleSelectionChange = (newSelectedRows: string[]) => {
    const selectedConsumers = meterReadingMissingList?.results
      .filter((meter) => newSelectedRows.includes(meter.meterNumber))
      .map((meter) => meter.consumerNumber);
    setSelectedMeters(newSelectedRows);
    onSelectionChange(selectedConsumers);
  };

  const toggleRowSelection = async (meterNumber: string) => {
    setPendingAction({ type: 'single', meterNumber });
    setShowConfirmationDialog(true);
  };

  const handleExcludeAll = async () => {
    setPendingAction({ type: 'all' });
    setShowConfirmationDialog(true);
  };

  const executeExclusion = async () => {
    if (!pendingAction) return;

    try {
      setIsExcluding(true);

      if (pendingAction.type === 'single' && pendingAction.meterNumber) {
        // Single meter exclusion
        const payload = {
          step: {
            step_working_on: 2,
            total_steps: 5,
          },
          excluded_consumers: [pendingAction.meterNumber],
        };

        await updateConsumer(payload);

        // Update local selection state
        const newSelectedMeters = selectedMeters.filter((id) => id !== pendingAction.meterNumber);
        setSelectedMeters(newSelectedMeters);
        onSelectionChange?.(newSelectedMeters);
      } else if (pendingAction.type === 'all') {
        // Exclude all
        const allConsumers = meterReadingMissingList?.results.map(
          (m) => m.consumerNumber
        ) ?? [];

        const payload = {
          step: {
            step_working_on: 2,
            total_steps: 5,
          },
          excluded_consumers: allConsumers,
        };

        await updateConsumer(payload);
        setSelectedMeters([]);
        onSelectionChange([]);
      }
    } catch (err) {
      console.error("Exclusion failed", err);
    } finally {
      setIsExcluding(false);
      setShowConfirmationDialog(false);
      setPendingAction(null);
    }
  };

  const handleProceed = () => {
    executeExclusion();
  };

  const handleCancel = () => {
    setShowConfirmationDialog(false);
    setPendingAction(null);
  };

  const missingMeterReadingsActions: TableAction<any>[] = [
    {
      label: "Exclude",
      onClick: (row: any) => toggleRowSelection(row.meterNumber),
    },
  ];

  const missingMeterReadingscolumns = [
    {
      key: "consumerNumber",
      header: "Consumer Number",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
    },
    {
      key: "meterNumber",
      header: "Meter No",
    },
    {
      key: "meterType",
      header: "Meter Type",
    },
    {
      key: "productCode",
      header: "Service",
    },
    {
      key: "previousReading",
      header: "Last Reading",
      width: "text-right",
    },
    {
      key: "lastReadingDate",
      header: "Last Reading Date",
    },
  ];

  return (
    <div className="p-4 bg-background">
      <div className="mb-4 flex justify-between">
        <div>
          <h3 className="text-xl font-bold">Missing Readings</h3>
          <p className="text-muted-foreground text-sm">
            Meters with no reading collected in the current cycle
          </p>
        </div>
        <Button
          onClick={handleExcludeAll}
          disabled={isExcluding}
          className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-medium text-base"
        >
          {isExcluding ? "Excluding..." : "Exclude All"}
        </Button>
      </div>

      <div className="border rounded-md overflow-auto max-h-[400px]">
        <GenericTable
          data={meterReadingMissingList?.results ?? []}
          columns={missingMeterReadingscolumns}
          rowKey="meterNumber"
          actions={missingMeterReadingsActions}
          actionsType="buttons"
          emptyMessage="No missing meter readings found"
          selectable={true}
          selectedRows={selectedMeters}
          onSelectionChange={handleSelectionChange}
          pagination={{
            currentPage,
            totalPages: Math.ceil(
              (meterReadingMissingList?.count || 0) / itemsPerPage
            ),
            totalItems: meterReadingMissingList?.count || 0,
            itemsPerPage,
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(
              currentPage * itemsPerPage,
              meterReadingMissingList?.count || 0
            ),
          }}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmationDialog}
        onOpenChange={setShowConfirmationDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Exclude Selected Bills
            </DialogTitle>
            <DialogDescription className="text-base">
              Exclude selected bills? They won't be generated in this billing cycle.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleProceed} disabled={isExcluding}>
              {isExcluding ? "Excluding..." : "Yes, Exclude"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};